import type { AudioAsset, Species, SoundType } from '../../types';
import { getSpecies, packPool } from '../region';
import { studyCodes, studyList } from '../ignored';
import { studyRemainder } from '../fsrs';
import { narrow } from './focus';
import { buildChoices, buildPairChoices } from './distractors';
import { DEFAULT_ROUND_LEN, newBirdShare } from './settings';

export interface SoundQuestion {
  /** The species being tested. */
  target: Species;
  /** The specific recording to play. */
  audio: AudioAsset;
  /** Answer choices (species codes), target included, in display order. */
  choices: string[];
}

const CHOICES = 4; // 1 correct + 3 distractors, matching Name Match

/**
 * The voices an ordinary round is allowed to play.
 *
 * Calls are what the hard mode adds, so the easy mode is the one that filters:
 * a call is the sound a bird makes when it is not announcing itself, short and
 * shared across half the family, and being handed one before the songs are
 * solid teaches nothing. Alarms stay in — they are loud, distinctive and
 * usually the first thing a beginner notices in the field.
 */
export const WITHOUT_CALLS: readonly SoundType[] = ['song', 'alarm'];

/** Whether a recording is one of the voices a round asked for. */
function allowed(a: AudioAsset, voices?: readonly SoundType[]): boolean {
  return !voices || voices.includes(a.type);
}

/**
 * Species codes with at least one recording — of an allowed voice, when the
 * round narrows them.
 *
 * This, not the full region, is what the study queue draws from: without it a
 * session spends slots on species whose question then comes back null and is
 * silently dropped, so a "10 question" round is quietly shorter. Mirrors
 * `photoPool()` in photo.ts, which narrows the same way for the same reason.
 */
export function soundPool(voices?: readonly SoundType[]): string[] {
  return studyList()
    .filter((s) => s.audio.some((a) => allowed(a, voices)))
    .map((s) => s.code);
}

/**
 * Pick a recording for a species, optionally constrained to a set of voices.
 *
 * Uniform over whatever survives the filter, which is the whole point of the
 * hard mode: a bird with six calls and one song is a bird you will mostly hear
 * calling, and the round should be weighted the way the recordings are rather
 * than dealing one voice at a fixed rate. The filter is honoured or nothing is
 * returned — deliberately no falling back to the full set, since a songs round
 * that quietly serves a call when the species has no song is not a songs round.
 */
function pickAudio(
  sp: Species,
  rnd: () => number,
  voices?: readonly SoundType[],
): AudioAsset | undefined {
  const from = sp.audio.filter((a) => allowed(a, voices));
  if (!from.length) return undefined;
  return from[Math.floor(rnd() * from.length)];
}

/**
 * Build a Sound ID session: an ordered list of questions from the FSRS study
 * queue. Species without the audio this session needs are never queued.
 *
 * `voices` is what the round may play, and it is the EASY mode that passes it:
 * `WITHOUT_CALLS` keeps the round on songs and alarms, while leaving it
 * undefined is the hard mode — every recording the bird has, calls included, in
 * whatever proportion the pack happens to hold them.
 *
 * `done` is a species the round has already asked outside the session — the
 * daily challenge card — so it is dropped and the round is one question shorter.
 *
 * `len` is the round length the setup screen chose, and the same screen's
 * choice of how much of the round meets new birds is read straight off the
 * settings; see quiz/settings.ts.
 */
export async function buildSoundSession(
  now: Date = new Date(),
  rnd: () => number = Math.random,
  voices?: readonly SoundType[],
  done?: string,
  len: number = DEFAULT_ROUND_LEN,
  /**
   * The birds this round is confined to, when it was started from a filtered
   * list rather than from the study grid — see core/quiz/focus.ts. Undefined is
   * an ordinary round over everything this mode can ask about.
   */
  focus?: readonly string[],
): Promise<SoundQuestion[]> {
  const queue = await studyRemainder(
    'sound',
    narrow(soundPool(voices), focus),
    now,
    len,
    done,
    await newBirdShare('sound'),
  );
  const questions: SoundQuestion[] = [];
  for (const code of queue) {
    const q = buildSoundQuestion(code, rnd, voices);
    if (q) questions.push(q);
  }
  return questions;
}

/**
 * Build a single Sound ID question for a known species code.
 *
 * `partner` is the pair drill's other bird — the species this one keeps being
 * called — and guarantees it is on the answer list. Everything else about the
 * question is unchanged, which is the point: the drill is the ordinary round
 * with one card spoken for.
 */
export function buildSoundQuestion(
  code: string,
  rnd: () => number = Math.random,
  voices?: readonly SoundType[],
  partner?: string,
): SoundQuestion | null {
  const target = getSpecies(code);
  if (!target) return null;
  const audio = pickAudio(target, rnd, voices);
  if (!audio) return null;
  const pool = packPool(code, studyCodes());
  const choices = partner
    ? buildPairChoices(code, partner, pool, CHOICES - 1, 'sound', rnd)
    : buildChoices(code, pool, CHOICES - 1, 'sound', rnd);
  return { target, audio, choices };
}
